import {
  css, Global,
} from '@emotion/react';
import {
  type ChangeEvent, type FC, Fragment, useCallback, useEffect, useMemo, useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { useTheme } from '~/_shared/themes/theme.hooks';
import { type Theme } from '~/_shared/themes/theme.model';
import { createCancelToken } from '~/_shared/utils/api/api.helpers';
import { copy } from '~/_shared/utils/collections/collections';
import { type Uuid } from '~/_shared/utils/createUuid';
import { downloadFile } from '~/_shared/utils/document/document.helpers';
import { EXPORT_REPORT_BOUNDARY_GROUP_CODENAME } from '~/exportReport/exportReport.constants';
import {
  createLocationExportReportDemographicsQueries,
  getReportDemographicDataFromResults,
  getReportDemographicLabelFromResults,
  getReportTemplateDemographicIds,
} from '~/exportReport/exportReportData.helpers';
import { ExportReportType } from '~/exportReport/exportReportType';
import {
  ExportReportTemplates, HighPriorityExportReportTemplates,
} from '~/exportReport/template/exportReportTemplates';
import type { ReportTemplate } from '~/exportReport/template/reportTemplate';
import { isReportFormulaIsTooComplex } from '~/exportReport/template/reportTemplate.helpers';
import { validateReportTemplate } from '~/exportReport/templateLoader/exportReportTemplateUploader.helpers';
import { ExportReportTemplateRendererComponent } from '~/exportReport/templateRenderer/exportReportTemplateRenderer.component';
import { MapListSortType } from '~/map/listing/mapListing.helpers';
import { getMapList } from '~/map/map.repository';
import {
  useBoundaryGroupsIsLoadingSelector, useBoundaryGroupsSelector,
} from '~/store/boundaryGroups/boundaryGroups.selectors';
import { createDemographicsRegionFromResponse } from '~/store/demographics/demographics.factory';
import {
  type DemographicsQueryBulkResults, type DemographicsResult, getDemographicsListAgs, getDemographicsQueryBulk,
} from '~/store/demographics/demographics.repository';
import { mapIdSet } from '~/store/mapId/mapId.actionCreators';
import { useClientIdSelector } from '~/store/selectors/useClientIdSelector';
import {
  type BuilderReportTemplate, type BuilderReportTemplateItem, type BuilderReportTemplateTableItem, type BuilderReportTemplateTableRow,
  createBlankReportTemplate, createBuilderReportTemplate, createReportTemplate, createTableRow, createTemplateItem, type DemographicOption,
  filterDemographicOptions,
  flattenDemographicOptions, getDemographicLabelMap, getInvalidDemographicDataRows, getTemplateFileName, moveArrayItem, type ReportTemplateDataRow, type ReportTemplateItem, type ReportTemplateTableRow,
} from './reportTemplateBuilder.helpers';

type TemplateItemType = ReportTemplateItem['type'];
type TableRowType = ReportTemplateTableRow['type'];
type ValidationResult = ReturnType<typeof validateReportTemplate>;
type BuilderValidationResult = Readonly<{
  isValid: boolean;
  error?: string;
}>;

const pageStyle = (theme: Theme) => css({
  '--rtb-background': theme.backgroundColors.secondary,
  '--rtb-panel-background': theme.backgroundColors.primary,
  '--rtb-panel-background-subtle': theme.backgroundColors.tertiary,
  '--rtb-panel-background-hover': theme.backgroundColors.secondaryHover,
  '--rtb-input-background': theme.dropdownColors.triggerBackground,
  '--rtb-input-background-hover': theme.dropdownColors.triggerBackgroundHover,
  '--rtb-text': theme.textColors.primary,
  '--rtb-text-muted': theme.textColors.secondary,
  '--rtb-border': theme.borderColors.primary,
  '--rtb-border-strong': theme.borderColors.tertiary,
  '--rtb-border-focus': theme.borderColors.primaryFocused,
  '--rtb-primary-background': theme.buttonColors.primaryBackground,
  '--rtb-primary-background-hover': theme.buttonColors.primaryBackgroundOnHover,
  '--rtb-primary-text': theme.buttonColors.primaryText,
  '--rtb-button-background': theme.buttonColors.tertiaryBackground,
  '--rtb-button-background-hover': theme.buttonColors.quaternaryBackgroundOnHover,
  '--rtb-button-text': theme.buttonColors.tertiaryText,
  '--rtb-danger': theme.textColors.danger,
  '--rtb-danger-background': theme.buttonColors.dangerBackground,
  '--rtb-danger-background-hover': theme.buttonColors.dangerBackgroundHover,
  '--rtb-success': theme.textColors.success,
  height: '100vh',
  minWidth: 0,
  display: 'grid',
  gridTemplateRows: 'auto 1fr',
  color: 'var(--rtb-text)',
  background: 'var(--rtb-background)',
  overflow: 'hidden',
  fontSize: 14,

  'h1, h2': {
    color: 'var(--rtb-text)',
    margin: 0,
    letterSpacing: 0,
  },

  h1: {
    fontSize: 20,
    lineHeight: '26px',
    marginBottom: 12,
  },

  h2: {
    fontSize: 16,
    lineHeight: '22px',
  },
});

const toolbarStyle = css({
  display: 'grid',
  gridTemplateColumns: 'minmax(280px, 520px) repeat(5, auto) minmax(120px, 1fr)',
  alignItems: 'center',
  gap: 10,
  padding: '10px 14px',
  borderBottom: '1px solid var(--rtb-border)',
  background: 'var(--rtb-panel-background)',
  minWidth: 0,
});

const splitStyle = css({
  display: 'grid',
  gridTemplateColumns: 'minmax(360px, 1fr) minmax(0, min(60%, 1500px))',
  minHeight: 0,
  minWidth: 0,
});

const paneStyle = css({
  minHeight: 0,
  minWidth: 0,
  overflow: 'auto',
  padding: 14,
});

const previewPaneStyle = css(paneStyle, {
  borderLeft: '1px solid var(--rtb-border)',
  background: 'var(--rtb-background)',
});

const sectionStyle = css({
  background: 'var(--rtb-panel-background)',
  border: '1px solid var(--rtb-border)',
  borderRadius: 6,
  marginBottom: 10,
  padding: 12,
});

const rowStyle = css({
  display: 'grid',
  gridTemplateColumns: '140px minmax(0, 1fr)',
  gap: 8,
  alignItems: 'center',
  marginTop: 8,
  marginBottom: 0,
  minWidth: 0,
});

const toolbarFieldStyle = css(rowStyle, {
  gridTemplateColumns: 'auto minmax(180px, 1fr)',
  marginTop: 0,
});

const compactRowStyle = css({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 8,
  alignItems: 'center',
  minWidth: 0,
});

const checkboxRowStyle = css(compactRowStyle, {
  marginTop: 8,
});

const sectionHeaderStyle = css(compactRowStyle, {
  justifyContent: 'space-between',
  marginBottom: 8,
});

const sectionHeaderActionsStyle = css(compactRowStyle, {
  marginLeft: 'auto',
});

const inlineAddWrapperStyle = css({
  marginTop: 8,
  paddingTop: 6,
});

const inlineAddPanelStyle = css({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 8,
  alignItems: 'center',
  marginTop: 6,
  padding: 8,
  border: '1px dashed var(--rtb-border)',
  borderRadius: 4,
  background: 'var(--rtb-panel-background)',

  select: {
    width: 220,
  },
});

const linkButtonStyle = css({
  border: 'none',
  background: 'transparent',
  color: 'var(--rtb-text-muted)',
  cursor: 'pointer',
  padding: 0,
  fontSize: 12,
  fontWeight: 600,

  ':hover': {
    color: 'var(--rtb-text)',
    textDecoration: 'underline',
  },
});

const editorHeaderStyle = css(compactRowStyle, {
  flexWrap: 'nowrap',
  alignItems: 'center',
});

const titleButtonStyle = css({
  border: 'none',
  background: 'transparent',
  color: 'var(--rtb-text)',
  cursor: 'pointer',
  flex: '1 1 auto',
  padding: 0,
  fontSize: 14,
  fontWeight: 700,
  textAlign: 'left',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',

  ':hover': {
    color: 'var(--rtb-text-muted)',
  },
});

const titleButtonErrorStyle = css(titleButtonStyle, {
  color: 'var(--rtb-danger)',
});

const editorActionsStyle = css(compactRowStyle, {
  marginLeft: 'auto',
  flexWrap: 'nowrap',
  flex: '0 0 auto',
});

const bodyStyle = css({
  marginTop: 8,
});

const itemStyle = css({
  border: '1px solid var(--rtb-border)',
  borderRadius: 6,
  padding: 10,
  marginTop: 8,
  background: 'var(--rtb-panel-background-subtle)',
});

const nestedItemStyle = css(itemStyle, {
  background: 'var(--rtb-panel-background)',
});

const labelStyle = css({
  fontWeight: 600,
  fontSize: 13,
  color: 'var(--rtb-text)',
});

const inputStyle = css({
  width: '100%',
  minWidth: 0,
  boxSizing: 'border-box',
  border: '1px solid var(--rtb-border)',
  borderRadius: 4,
  padding: '6px 8px',
  fontSize: 14,
  lineHeight: '20px',
  color: 'var(--rtb-text)',
  background: 'var(--rtb-input-background)',
  outline: 'none',

  '&:hover': {
    background: 'var(--rtb-input-background-hover)',
  },

  '&:focus': {
    borderColor: 'var(--rtb-border-focus)',
    boxShadow: '0 0 0 1px var(--rtb-border-focus)',
  },

  '&::placeholder': {
    color: 'var(--rtb-text-muted)',
  },

  option: {
    color: 'var(--rtb-text)',
    background: 'var(--rtb-panel-background)',
  },
});

const inputDropdownWrapperStyle = css({
  position: 'relative',
  minWidth: 0,
});

const inputDropdownStyle = css({
  position: 'absolute',
  zIndex: 20,
  top: 'calc(100% + 4px)',
  left: 0,
  right: 0,
  maxHeight: 220,
  overflow: 'auto',
  border: '1px solid var(--rtb-border)',
  borderRadius: 4,
  background: 'var(--rtb-panel-background)',
  boxShadow: '0 10px 24px rgba(0, 0, 0, .28)',
});

const inputDropdownOptionStyle = css({
  display: 'block',
  width: '100%',
  border: 'none',
  borderBottom: '1px solid var(--rtb-border)',
  background: 'transparent',
  color: 'var(--rtb-text)',
  cursor: 'pointer',
  padding: '7px 9px',
  textAlign: 'left',
  fontSize: 12,

  ':hover, :focus': {
    background: 'var(--rtb-panel-background-hover)',
  },

  ':last-of-type': {
    borderBottom: 'none',
  },
});

const inputDropdownMetaStyle = css({
  display: 'block',
  marginTop: 2,
  color: 'var(--rtb-text-muted)',
});

const buttonStyle = css({
  border: '1px solid var(--rtb-border)',
  borderRadius: 4,
  background: 'var(--rtb-button-background)',
  color: 'var(--rtb-button-text)',
  minHeight: 32,
  padding: '5px 10px',
  cursor: 'pointer',
  fontWeight: 600,

  ':hover:not(:disabled)': {
    background: 'var(--rtb-button-background-hover)',
  },

  ':disabled': {
    opacity: .45,
    cursor: 'not-allowed',
  },
});

const primaryButtonStyle = css(buttonStyle, {
  background: 'var(--rtb-primary-background)',
  borderColor: 'var(--rtb-primary-background)',
  color: 'var(--rtb-primary-text)',

  ':hover:not(:disabled)': {
    background: 'var(--rtb-primary-background-hover)',
  },
});

const dangerButtonStyle = css(buttonStyle, {
  borderColor: 'var(--rtb-danger)',
  color: 'var(--rtb-danger)',
});

const mutedTextStyle = css({
  color: 'var(--rtb-text-muted)',
  fontSize: 12,
});

const errorTextStyle = css(mutedTextStyle, {
  color: 'var(--rtb-danger)',
  fontWeight: 600,
});

const checkboxStyle = css({
  accentColor: 'var(--rtb-primary-background)',
});

const previewWrapperStyle = css({
  width: 'min(100%, 940px)',
  minWidth: 0,
  margin: '0 auto',
});

const rawYamlStyle = css({
  minHeight: 180,
  resize: 'vertical',
  fontFamily: 'monospace',
});

const statusStyle = css(mutedTextStyle, {
  justifySelf: 'end',
  color: 'var(--rtb-success)',
  fontWeight: 600,
});

const toolbarCheckboxStyle = css(compactRowStyle, {
  color: 'var(--rtb-text)',
  whiteSpace: 'nowrap',
});

const itemTypeOptions: readonly TemplateItemType[] = ['map', 'title', 'table', 'line-break', 'page-break'];
const tableRowTypeOptions: readonly TableRowType[] = ['data-row', 'title-row', 'empty-row'];
const formatTypeOptions: ReadonlyArray<NonNullable<ReportTemplateDataRow['format_type']>> = [
  'CURRENCY', 'COUNT', 'PERCENT', 'DECIMAL',
];

const getInvalidDemographicDataRowLocation = (row: ReturnType<typeof getInvalidDemographicDataRows>[number]): string => {
  const tableTitle = row.tableTitle?.trim();
  const tableLocation = tableTitle
    ? `table ${row.tableIndex + 1} "${tableTitle}"`
    : `table ${row.tableIndex + 1}`;

  return `${tableLocation}, row ${row.rowIndex + 1}`;
};

const getComplexFormulaRowUuids = (template: BuilderReportTemplate): ReadonlySet<Uuid> => new Set(template.items.flatMap(item => item.type === 'table'
  ? item.content.flatMap(row => row.type === 'data-row' && row.formula && isReportFormulaIsTooComplex(row.formula) ? [row.uuid] : [])
  : []));

const getDefaultBuilderTemplate = (): ReportTemplate => knownTemplates.find(template => template.id === 'xp5') ?? createBlankReportTemplate();

const previewCenter = { lat: 34.052235, lng: -118.243683 };
const previewTableColumns = [
  { id: 'radius5', heading: '5 km', secondaryHeading: 'Preview' },
  { id: 'radius10', heading: '10 km', secondaryHeading: 'Preview' },
  { id: 'radius15', heading: '15 km', secondaryHeading: 'Preview' },
];

const knownTemplates = [...new Map([
  ...HighPriorityExportReportTemplates,
  ...ExportReportTemplates,
].map(template => [template.id, template])).values()];

export const ReportTemplateBuilderPageComponent: FC = () => {
  const defaultTemplate = useMemo(() => createBuilderReportTemplate(getDefaultBuilderTemplate()), []);
  const theme = useTheme();
  const dispatch = useDispatch();
  const clientId = useClientIdSelector();
  const boundaryGroups = useBoundaryGroupsSelector();
  const isBoundaryGroupsLoading = useBoundaryGroupsIsLoadingSelector();
  const [draftTemplate, setDraftTemplate] = useState<BuilderReportTemplate>(defaultTemplate);
  const [selectedTemplateId, setSelectedTemplateId] = useState(draftTemplate.id);
  const [demographicOptions, setDemographicOptions] = useState<ReadonlyArray<DemographicOption>>([]);
  const [isDemographicsLoading, setIsDemographicsLoading] = useState(false);
  const [demographicsError, setDemographicsError] = useState<string | null>(null);
  const [selectedRegion, setSelectedRegion] = useState<string>('US');
  const [useActualPreviewData, setUseActualPreviewData] = useState(false);
  const [previewMapId, setPreviewMapId] = useState<number | null>(null);
  const [isPreviewMapLoading, setIsPreviewMapLoading] = useState(false);
  const [actualPreviewData, setActualPreviewData] = useState<DemographicsQueryBulkResults | null>(null);
  const [isActualPreviewDataLoading, setIsActualPreviewDataLoading] = useState(false);
  const [actualPreviewDataError, setActualPreviewDataError] = useState<string | null>(null);
  const [yamlText, setYamlText] = useState('');
  const [yamlError, setYamlError] = useState<string | null>(null);
  const [showMapPreview, setShowMapPreview] = useState(false);
  const [expandedItems, setExpandedItems] = useState<ReadonlySet<Uuid>>(() => new Set());
  const [expandedRows, setExpandedRows] = useState<ReadonlySet<Uuid>>(() => new Set());
  const [addItemAfterIndex, setAddItemAfterIndex] = useState<number | null>(null);
  const [addItemTypeByIndex, setAddItemTypeByIndex] = useState<Record<number, TemplateItemType>>({});
  const [addRowAfterKey, setAddRowAfterKey] = useState<Uuid | null>(null);
  const [addRowTypeByKey, setAddRowTypeByKey] = useState<Record<string, TableRowType>>({});

  const availableRegions = useMemo<ReadonlyArray<string>>(() => {
    const regions = new Set(demographicOptions.map(o => o.region));
    return [...regions].sort();
  }, [demographicOptions]);

  const filteredDemographicOptions = useMemo<ReadonlyArray<DemographicOption>>(
    () => selectedRegion ? demographicOptions.filter(o => o.region === selectedRegion) : demographicOptions,
    [demographicOptions, selectedRegion]
  );
  const boundaryGroupId = useMemo(() => {
    return boundaryGroups.find(boundaryGroup => boundaryGroup.codename === EXPORT_REPORT_BOUNDARY_GROUP_CODENAME)?.id ?? null;
  }, [boundaryGroups]);
  const reportTemplate = useMemo(() => createReportTemplate(draftTemplate), [draftTemplate]);
  const invalidDemographicDataRows = useMemo(
    () => getInvalidDemographicDataRows(draftTemplate, filteredDemographicOptions),
    [draftTemplate, filteredDemographicOptions]
  );
  const invalidDemographicRowUuids = useMemo<ReadonlySet<Uuid>>(() => {
    if (isDemographicsLoading || demographicsError) {
      return new Set();
    }

    return new Set(invalidDemographicDataRows.map(row => row.rowUuid));
  }, [demographicsError, invalidDemographicDataRows, isDemographicsLoading]);
  const complexFormulaRowUuids = useMemo(() => getComplexFormulaRowUuids(draftTemplate), [draftTemplate]);

  const validationResult = useMemo<BuilderValidationResult>(() => {
    const schemaValidationResult: ValidationResult = validateReportTemplate(reportTemplate);
    if (!schemaValidationResult.isValid) {
      return schemaValidationResult;
    }

    if (isDemographicsLoading) {
      return {
        isValid: false,
        error: 'Loading demographics before validating data-row IDs.',
      };
    }

    if (demographicsError) {
      return {
        isValid: false,
        error: 'Unable to validate data-row IDs until demographics load.',
      };
    }

    const firstInvalidRow = invalidDemographicDataRows.find(() => true);
    if (!firstInvalidRow) {
      return { isValid: true };
    }

    return {
      isValid: false,
      error: `Unknown demographic ID "${firstInvalidRow.id}" in ${getInvalidDemographicDataRowLocation(firstInvalidRow)}. Add a formula or choose an AGS demographic ID.`,
    };
  }, [demographicsError, invalidDemographicDataRows, isDemographicsLoading, reportTemplate]);
  const demographicLabelMap = useMemo(() => getDemographicLabelMap(filteredDemographicOptions), [filteredDemographicOptions]);
  const previewTemplate = useMemo<ReportTemplate>(() => {
    if (showMapPreview) {
      return reportTemplate;
    }

    return {
      ...reportTemplate,
      items: reportTemplate.items.map(item => item.type === 'map'
        ? { type: 'title', content: 'Map preview placeholder' }
        : item),
    };
  }, [reportTemplate, showMapPreview]);

  const getActualPreviewDemographicData = useCallback((columnId: string, demographicId: string): DemographicsResult | null => {
    return getReportDemographicDataFromResults(actualPreviewData, columnId, demographicId);
  }, [actualPreviewData]);

  const getPreviewDemographicLabel = useCallback((demographicId: string): string | null => {
    if (useActualPreviewData) {
      return getReportDemographicLabelFromResults(actualPreviewData, demographicId) ?? demographicLabelMap.get(demographicId) ?? null;
    }

    return demographicLabelMap.get(demographicId) ?? null;
  }, [actualPreviewData, demographicLabelMap, useActualPreviewData]);

  useEffect(() => {
    let isActive = true;

    if (!clientId) {
      setDemographicsError('Sign in with a client before loading AGS demographics.');
      return;
    }

    setIsDemographicsLoading(true);
    setDemographicsError(null);

    getDemographicsListAgs(clientId)
      .then(response => {
        if (!isActive) {
          return;
        }

        setDemographicOptions(flattenDemographicOptions(response.data.map(createDemographicsRegionFromResponse)));
      })
      .catch(() => {
        if (isActive) {
          setDemographicsError('Unable to load AGS demographics.');
        }
      })
      .finally(() => {
        if (isActive) {
          setIsDemographicsLoading(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, [clientId]);

  useEffect(() => {
    if (!clientId) {
      setPreviewMapId(null);
      return;
    }

    let isActive = true;
    const cancelToken = createCancelToken();
    setIsPreviewMapLoading(true);

    getMapList(clientId, {
      page: 1,
      per_page: 1,
      sort: MapListSortType.Updated_DESC,
    }, cancelToken.token)
      .then(response => {
        if (isActive) {
          const mapId = response.data.list[0]?.id ?? null;
          setPreviewMapId(mapId);
          dispatch(mapIdSet(mapId));
        }
      })
      .catch(() => {
        if (isActive) {
          setPreviewMapId(null);
          dispatch(mapIdSet(null));
        }
      })
      .finally(() => {
        if (isActive) {
          setIsPreviewMapLoading(false);
        }
      });

    return () => {
      isActive = false;
      cancelToken.cancel();
    };
  }, [clientId, dispatch]);

  useEffect(() => {
    let isActive = true;
    setActualPreviewDataError(null);

    if (!useActualPreviewData) {
      setActualPreviewData(null);
      setIsActualPreviewDataLoading(false);
      return () => {
        isActive = false;
      };
    }

    if (!clientId) {
      setActualPreviewData(null);
      setIsActualPreviewDataLoading(false);
      setActualPreviewDataError('Sign in with a client before fetching preview data.');
      return () => {
        isActive = false;
      };
    }

    if (isPreviewMapLoading) {
      setActualPreviewData(null);
      setIsActualPreviewDataLoading(true);
      return () => {
        isActive = false;
      };
    }

    if (isBoundaryGroupsLoading) {
      setActualPreviewData(null);
      setIsActualPreviewDataLoading(true);
      return () => {
        isActive = false;
      };
    }

    if (!previewMapId) {
      setActualPreviewData(null);
      setIsActualPreviewDataLoading(false);
      setActualPreviewDataError('No saved maps were found for fetching preview data.');
      return () => {
        isActive = false;
      };
    }

    if (!boundaryGroupId) {
      setActualPreviewData(null);
      setIsActualPreviewDataLoading(false);
      setActualPreviewDataError('Unable to load boundary groups for fetching preview data.');
      return () => {
        isActive = false;
      };
    }

    const demographicIds = getReportTemplateDemographicIds(reportTemplate);
    if (!demographicIds.length) {
      setActualPreviewData(null);
      setIsActualPreviewDataLoading(false);
      setActualPreviewDataError('Add at least one data row before fetching preview data.');
      return () => {
        isActive = false;
      };
    }

    setIsActualPreviewDataLoading(true);

    getDemographicsQueryBulk(clientId, {
      queries: createLocationExportReportDemographicsQueries({
        boundaryGroupId,
        center: previewCenter,
        demographicIds,
        mapId: previewMapId,
        tableColumns: previewTableColumns,
      }),
    })
      .then(response => {
        if (isActive) {
          setActualPreviewData(response);
        }
      })
      .catch(() => {
        if (isActive) {
          setActualPreviewData(null);
          setActualPreviewDataError('Unable to fetch actual preview data.');
        }
      })
      .finally(() => {
        if (isActive) {
          setIsActualPreviewDataLoading(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, [boundaryGroupId, clientId, isBoundaryGroupsLoading, isPreviewMapLoading, previewMapId, reportTemplate, useActualPreviewData]);

  const updateTemplate = useCallback((updater: (template: BuilderReportTemplate) => BuilderReportTemplate) => {
    setDraftTemplate(currentTemplate => updater(currentTemplate));
  }, []);

  const updateTemplateField = useCallback((field: 'id' | 'name', value: string) => {
    updateTemplate(template => ({ ...template, [field]: value }));
  }, [updateTemplate]);

  const loadTemplate = useCallback((templateId: string) => {
    const template = knownTemplates.find(template => template.id === templateId);
    if (!template) {
      return;
    }

    setSelectedTemplateId(template.id);
    setDraftTemplate(createBuilderReportTemplate(template));
    setExpandedItems(new Set());
    setExpandedRows(new Set());
    setAddItemAfterIndex(null);
    setAddRowAfterKey(null);
  }, []);

  const createNewTemplate = useCallback(() => {
    const template = createBuilderReportTemplate(createBlankReportTemplate());
    setSelectedTemplateId(template.id);
    setDraftTemplate(template);
    setExpandedItems(new Set());
    setExpandedRows(new Set());
    setAddItemAfterIndex(null);
    setAddRowAfterKey(null);
  }, []);

  const toggleItemCollapsed = useCallback((itemId: Uuid) => {
    setExpandedItems(currentItems => copy.andTogglePresence(currentItems, itemId));
  }, []);

  const toggleRowCollapsed = useCallback((rowKey: Uuid) => {
    setExpandedRows(currentRows => copy.andTogglePresence(currentRows, rowKey));
  }, []);

  const collapseAllEditors = useCallback(() => {
    setExpandedItems(new Set());
    setExpandedRows(new Set());
  }, []);

  const expandAllEditors = useCallback(() => {
    setExpandedItems(new Set(draftTemplate.items.map(item => item.uuid)));
    setExpandedRows(new Set(draftTemplate.items.flatMap(item => item.type === 'table'
      ? item.content.map(row => row.uuid)
      : [])));
  }, [draftTemplate.items]);

  const updateItem = useCallback((itemIndex: number, updater: (item: BuilderReportTemplateItem) => BuilderReportTemplateItem) => {
    updateTemplate(template => ({
      ...template,
      items: template.items.map((item, index) => index === itemIndex ? updater(item) : item),
    }));
  }, [updateTemplate]);

  const updateTableRow = useCallback((itemIndex: number, rowIndex: number, updater: (row: BuilderReportTemplateTableRow) => BuilderReportTemplateTableRow) => {
    updateItem(itemIndex, item => {
      if (item.type !== 'table') {
        return item;
      }

      return {
        ...item,
        content: item.content.map((row, index) => index === rowIndex ? updater(row) : row),
      };
    });
  }, [updateItem]);

  const addItemAfter = useCallback((afterIndex: number, type: TemplateItemType) => {
    const item = createTemplateItem(type);

    updateTemplate(template => {
      const items = [...template.items];
      items.splice(afterIndex + 1, 0, item);

      return { ...template, items };
    });
    setExpandedItems(currentItems => new Set([...currentItems, item.uuid]));
    setAddItemAfterIndex(null);
  }, [updateTemplate]);

  const removeItem = useCallback((itemIndex: number) => {
    const item = draftTemplate.items[itemIndex];

    updateTemplate(template => ({ ...template, items: template.items.filter((_, index) => index !== itemIndex) }));

    if (item) {
      setExpandedItems(currentItems => copy.andRemove(currentItems, [item.uuid]));
      setExpandedRows(currentRows => item.type === 'table'
        ? copy.andRemove(currentRows, item.content.map(row => row.uuid))
        : currentRows);
    }
  }, [draftTemplate.items, updateTemplate]);

  const moveItem = useCallback((itemIndex: number, direction: -1 | 1) => {
    updateTemplate(template => ({ ...template, items: moveArrayItem(template.items, itemIndex, itemIndex + direction) }));
  }, [updateTemplate]);

  const addTableRowAfter = useCallback((itemIndex: number, afterRowIndex: number, type: TableRowType) => {
    const row = createTableRow(type);

    updateItem(itemIndex, item => {
      if (item.type !== 'table') {
        return item;
      }

      const content = [...item.content];
      content.splice(afterRowIndex + 1, 0, row);

      return { ...item, content };
    });
    setExpandedRows(currentRows => new Set([...currentRows, row.uuid]));
    setAddRowAfterKey(null);
  }, [updateItem]);

  const removeTableRow = useCallback((itemIndex: number, rowIndex: number) => {
    const item = draftTemplate.items[itemIndex];
    const row = item?.type === 'table' ? item.content[rowIndex] : null;

    updateItem(itemIndex, item => item.type === 'table'
      ? { ...item, content: item.content.filter((_, index) => index !== rowIndex) }
      : item);

    if (row) {
      setExpandedRows(currentRows => copy.andRemove(currentRows, [row.uuid]));
    }
  }, [draftTemplate.items, updateItem]);

  const moveTableRow = useCallback((itemIndex: number, rowIndex: number, direction: -1 | 1) => {
    updateItem(itemIndex, item => item.type === 'table'
      ? { ...item, content: moveArrayItem(item.content, rowIndex, rowIndex + direction) }
      : item);
  }, [updateItem]);

  const exportYaml = useCallback(async () => {
    const { stringify } = await import('yaml');
    const yaml = stringify(reportTemplate);
    const blobUrl = URL.createObjectURL(new Blob([yaml], { type: 'application/x-yaml' }));
    downloadFile(blobUrl, { fileName: getTemplateFileName(reportTemplate) });
    URL.revokeObjectURL(blobUrl);
  }, [reportTemplate]);

  const refreshYamlText = useCallback(async () => {
    const { stringify } = await import('yaml');
    setYamlText(stringify(reportTemplate));
  }, [reportTemplate]);

  const applyYamlText = useCallback(async () => {
    try {
      const { parse } = await import('yaml');
      const parsedTemplate = parse(yamlText) as Partial<ReportTemplate>;
      const result = validateReportTemplate(parsedTemplate);
      if (!result.isValid) {
        setYamlError(result.error ?? 'Template YAML is invalid.');
        return;
      }

      setDraftTemplate(createBuilderReportTemplate(parsedTemplate as ReportTemplate));
      setSelectedTemplateId(parsedTemplate.id ?? 'custom-template');
      setYamlError(null);
    }
    catch (error) {
      setYamlError(error instanceof Error ? error.message : 'Unable to parse template YAML.');
    }
  }, [yamlText]);

  return (
    <>
      <Global styles={{ '#root': { overflow: 'auto !important' } }} />
      <main css={pageStyle(theme)} aria-label="PDF report template builder">
        <div css={toolbarStyle} aria-label="Report template builder toolbar">
          <Field label="Load template" style={toolbarFieldStyle}>
            <select
              css={inputStyle}
              aria-label="Load existing report template"
              value={selectedTemplateId}
              onChange={(event) => loadTemplate(event.target.value)}
            >
              {!knownTemplates.some(template => template.id === selectedTemplateId) && (
                <option value={selectedTemplateId}>{`${draftTemplate.name} (${draftTemplate.id})`}</option>
              )}
              {knownTemplates.map(template => (
                <option key={template.id} value={template.id}>{`${template.name} (${template.id})`}</option>
              ))}
            </select>
          </Field>

          <button
            css={buttonStyle}
            type="button"
            aria-label="Create new report template"
            onClick={createNewTemplate}
          >New
          </button>
          <button
            css={primaryButtonStyle}
            type="button"
            aria-label="Export report template YAML"
            onClick={exportYaml}
          >Export YAML
          </button>
          <button
            css={buttonStyle}
            type="button"
            aria-label="Refresh raw YAML from current template"
            onClick={refreshYamlText}
          >Refresh YAML
          </button>

          <label css={toolbarCheckboxStyle}>
            <input
              css={checkboxStyle}
              type="checkbox"
              aria-label="Show real map preview"
              checked={showMapPreview}
              onChange={() => setShowMapPreview(value => !value)}
            />
            Show maps
          </label>

          <label css={toolbarCheckboxStyle}>
            <input
              css={checkboxStyle}
              type="checkbox"
              aria-label="Fetch actual preview data"
              checked={useActualPreviewData}
              onChange={() => setUseActualPreviewData(value => !value)}
            />
            Fetch data
          </label>

          <div css={statusStyle} aria-label="Template validation status">
            {actualPreviewDataError ?? (isActualPreviewDataLoading ? 'Loading preview data...' : validationResult.isValid ? 'Template is valid' : validationResult.error)}
          </div>
        </div>

        <div css={splitStyle} aria-label="Vertical split template editor and preview">
          <section css={paneStyle} aria-label="Template editor pane">
            <section css={sectionStyle} aria-label="Template metadata editor">
              <h1>PDF Report Template Builder</h1>
              <TextField
                label="Template ID"
                ariaLabel="Template ID"
                value={draftTemplate.id}
                onChange={value => updateTemplateField('id', value)}
              />
              <TextField
                label="Template Name"
                ariaLabel="Template name"
                value={draftTemplate.name}
                onChange={value => updateTemplateField('name', value)}
              />
            </section>

            <section css={sectionStyle} aria-label="AGS demographics search">
              <h2>Demographics</h2>
              <div css={[mutedTextStyle, { marginTop: 8 }]} aria-label="AGS demographics loading status">
                {isDemographicsLoading && 'Loading AGS demographics...'}
                {demographicsError && demographicsError}
                {!isDemographicsLoading && !demographicsError && (
                  selectedRegion
                    ? `${filteredDemographicOptions.length} of ${demographicOptions.length} demographics loaded (${selectedRegion})`
                    : `${demographicOptions.length} demographics loaded`
                )}
              </div>
              {!isDemographicsLoading && !demographicsError && availableRegions.length > 0 && (
                <div css={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                  <label css={labelStyle} htmlFor="rtb-region-select">Region</label>
                  <select
                    id="rtb-region-select"
                    css={[inputStyle, { width: 'auto' }]}
                    value={selectedRegion}
                    onChange={e => setSelectedRegion(e.target.value)}
                  >
                    <option value="">All regions</option>
                    {availableRegions.map(region => (
                      <option key={region} value={region}>{region}</option>
                    ))}
                  </select>
                </div>
              )}
            </section>

            <section css={sectionStyle} aria-label="Template item list editor">
              <div css={sectionHeaderStyle}>
                <h2>Template Items</h2>
                <div css={sectionHeaderActionsStyle}>
                  <button
                    css={linkButtonStyle}
                    type="button"
                    aria-label="Collapse all template items and table rows"
                    onClick={collapseAllEditors}
                  >Collapse all
                  </button>
                  <button
                    css={linkButtonStyle}
                    type="button"
                    aria-label="Expand all template items and table rows"
                    onClick={expandAllEditors}
                  >Expand all
                  </button>
                </div>
              </div>

              {!draftTemplate.items.length && (
                <InlineAddItemControl
                  afterIndex={-1}
                  isOpen={addItemAfterIndex === -1}
                  selectedType={addItemTypeByIndex[-1] ?? 'table'}
                  onToggle={() => setAddItemAfterIndex(addItemAfterIndex === -1 ? null : -1)}
                  onTypeChange={type => setAddItemTypeByIndex(values => ({ ...values, [-1]: type }))}
                  onAdd={type => addItemAfter(-1, type)}
                />
              )}

              {draftTemplate.items.map((item, itemIndex) => (
                <Fragment key={item.uuid}>
                  <TemplateItemEditor
                    item={item}
                    itemIndex={itemIndex}
                    itemCount={draftTemplate.items.length}
                    demographicOptions={filteredDemographicOptions}
                    demographicLabelMap={demographicLabelMap}
                    isCollapsed={!expandedItems.has(item.uuid)}
                    addRowAfterKey={addRowAfterKey}
                    addRowTypeByKey={addRowTypeByKey}
                    expandedRows={expandedRows}
                    complexFormulaRowUuids={complexFormulaRowUuids}
                    invalidDemographicRowUuids={invalidDemographicRowUuids}
                    onToggleCollapsed={() => toggleItemCollapsed(item.uuid)}
                    onUpdate={updater => updateItem(itemIndex, updater)}
                    onMove={direction => moveItem(itemIndex, direction)}
                    onRemove={() => removeItem(itemIndex)}
                    onUpdateRow={(rowIndex, updater) => updateTableRow(itemIndex, rowIndex, updater)}
                    onMoveRow={(rowIndex, direction) => moveTableRow(itemIndex, rowIndex, direction)}
                    onRemoveRow={rowIndex => removeTableRow(itemIndex, rowIndex)}
                    onToggleRowCollapsed={toggleRowCollapsed}
                    onToggleAddRow={(rowKey) => setAddRowAfterKey(addRowAfterKey === rowKey ? null : rowKey)}
                    onRowTypeChange={(rowKey, type) => setAddRowTypeByKey(values => ({ ...values, [rowKey]: type }))}
                    onAddRowAfter={(rowIndex, type) => addTableRowAfter(itemIndex, rowIndex, type)}
                  />
                  <InlineAddItemControl
                    afterIndex={itemIndex}
                    isOpen={addItemAfterIndex === itemIndex}
                    selectedType={addItemTypeByIndex[itemIndex] ?? 'table'}
                    onToggle={() => setAddItemAfterIndex(addItemAfterIndex === itemIndex ? null : itemIndex)}
                    onTypeChange={type => setAddItemTypeByIndex(values => ({ ...values, [itemIndex]: type }))}
                    onAdd={type => addItemAfter(itemIndex, type)}
                  />
                </Fragment>
              ))}
            </section>

            <section css={sectionStyle} aria-label="Raw YAML editor">
              <h2>Raw YAML</h2>
              <textarea
                css={[inputStyle, rawYamlStyle]}
                aria-label="Raw report template YAML"
                value={yamlText}
                onChange={(event) => setYamlText(event.target.value)}
              />
              <div css={compactRowStyle}>
                <button
                  css={buttonStyle}
                  type="button"
                  aria-label="Apply raw YAML to template draft"
                  onClick={applyYamlText}
                >Apply YAML
                </button>
                {yamlError && <span css={mutedTextStyle} aria-label="Raw YAML error">{yamlError}</span>}
              </div>
            </section>
          </section>

          <section css={previewPaneStyle} aria-label="Template preview pane">
            <div css={previewWrapperStyle}>
              <ExportReportTemplateRendererComponent
                template={previewTemplate}
                tableColumns={previewTableColumns}
                summaryProps={{
                  latLng: previewCenter,
                  heading: draftTemplate.name,
                  semiHeading: draftTemplate.id,
                  description: 'Template preview mode',
                }}
                mapProps={{
                  type: ExportReportType.Location,
                  center: previewCenter,
                }}
                isPreviewMode={!useActualPreviewData}
                getDemographicData={useActualPreviewData ? getActualPreviewDemographicData : () => null}
                getDemographicLabel={getPreviewDemographicLabel}
              />
            </div>
          </section>
        </div>
      </main>
    </>
  );
};

type FieldProps = Readonly<{
  label: string;
  children: React.ReactNode;
  style?: ReturnType<typeof css>;
}>;

const Field = ({ label, children, style }: FieldProps) => (
  <label css={style ?? rowStyle}>
    <span css={labelStyle}>{label}</span>
    {children}
  </label>
);

type TextFieldProps = Readonly<{
  label: string;
  ariaLabel: string;
  value: string;
  onChange: (value: string) => void;
  list?: string;
}>;

const TextField = ({ ariaLabel, label, list, onChange, value }: TextFieldProps) => (
  <Field label={label}>
    <input
      css={inputStyle}
      aria-label={ariaLabel}
      list={list}
      value={value}
      onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event.target.value)}
    />
  </Field>
);

type TemplateItemEditorProps = Readonly<{
  item: BuilderReportTemplateItem;
  itemIndex: number;
  itemCount: number;
  demographicOptions: ReadonlyArray<DemographicOption>;
  demographicLabelMap: ReadonlyMap<string, string>;
  isCollapsed: boolean;
  addRowAfterKey: Uuid | null;
  addRowTypeByKey: Readonly<Record<string, TableRowType>>;
  expandedRows: ReadonlySet<Uuid>;
  complexFormulaRowUuids: ReadonlySet<Uuid>;
  invalidDemographicRowUuids: ReadonlySet<Uuid>;
  onToggleCollapsed: () => void;
  onUpdate: (updater: (item: BuilderReportTemplateItem) => BuilderReportTemplateItem) => void;
  onMove: (direction: -1 | 1) => void;
  onRemove: () => void;
  onUpdateRow: (rowIndex: number, updater: (row: BuilderReportTemplateTableRow) => BuilderReportTemplateTableRow) => void;
  onMoveRow: (rowIndex: number, direction: -1 | 1) => void;
  onRemoveRow: (rowIndex: number) => void;
  onToggleRowCollapsed: (rowKey: Uuid) => void;
  onToggleAddRow: (rowKey: Uuid) => void;
  onRowTypeChange: (rowKey: Uuid, type: TableRowType) => void;
  onAddRowAfter: (rowIndex: number, type: TableRowType) => void;
}>;

const TemplateItemEditor = ({
  addRowAfterKey, addRowTypeByKey, complexFormulaRowUuids, expandedRows, demographicLabelMap, demographicOptions, isCollapsed, item, itemCount, itemIndex,
  invalidDemographicRowUuids,
  onAddRowAfter, onMove, onMoveRow, onRemove, onRemoveRow, onRowTypeChange, onToggleAddRow, onToggleCollapsed,
  onToggleRowCollapsed, onUpdate, onUpdateRow,
}: TemplateItemEditorProps) => (
  <article css={itemStyle} aria-label={`Template item ${itemIndex + 1} editor`}>
    <div css={editorHeaderStyle}>
      <button
        css={titleButtonStyle}
        type="button"
        aria-label={`${isCollapsed ? 'Expand' : 'Collapse'} template item ${itemIndex + 1}`}
        aria-expanded={!isCollapsed}
        onClick={onToggleCollapsed}
      >{`${isCollapsed ? '+' : '-'} Item ${itemIndex + 1}: ${getItemSummary(item)}`}
      </button>
      <div css={editorActionsStyle}>
        <button
          css={buttonStyle}
          type="button"
          aria-label={`Move template item ${itemIndex + 1} up`}
          disabled={itemIndex === 0}
          onClick={() => onMove(-1)}
        >Up
        </button>
        <button
          css={buttonStyle}
          type="button"
          aria-label={`Move template item ${itemIndex + 1} down`}
          disabled={itemIndex === itemCount - 1}
          onClick={() => onMove(1)}
        >Down
        </button>
        <button
          css={dangerButtonStyle}
          type="button"
          aria-label={`Remove template item ${itemIndex + 1}`}
          onClick={onRemove}
        >Remove
        </button>
      </div>
    </div>

    {!isCollapsed && (
      <div css={bodyStyle}>
        {item.type === 'title' && (
          <TextField
            label="Content"
            ariaLabel={`Template item ${itemIndex + 1} title content`}
            value={item.content}
            onChange={content => onUpdate(() => ({ ...item, content }))}
          />
        )}

        {item.type === 'table' && (
          <TableEditor
            item={item}
            itemIndex={itemIndex}
            demographicOptions={demographicOptions}
            demographicLabelMap={demographicLabelMap}
            addRowAfterKey={addRowAfterKey}
            addRowTypeByKey={addRowTypeByKey}
            complexFormulaRowUuids={complexFormulaRowUuids}
            expandedRows={expandedRows}
            invalidDemographicRowUuids={invalidDemographicRowUuids}
            onUpdate={table => onUpdate(() => table)}
            onUpdateRow={onUpdateRow}
            onMoveRow={onMoveRow}
            onRemoveRow={onRemoveRow}
            onToggleRowCollapsed={onToggleRowCollapsed}
            onToggleAddRow={onToggleAddRow}
            onRowTypeChange={onRowTypeChange}
            onAddRowAfter={onAddRowAfter}
          />
        )}
      </div>
    )}
  </article>
);

type TableEditorProps = Readonly<{
  item: BuilderReportTemplateTableItem;
  itemIndex: number;
  demographicOptions: ReadonlyArray<DemographicOption>;
  demographicLabelMap: ReadonlyMap<string, string>;
  addRowAfterKey: Uuid | null;
  addRowTypeByKey: Readonly<Record<string, TableRowType>>;
  complexFormulaRowUuids: ReadonlySet<Uuid>;
  expandedRows: ReadonlySet<Uuid>;
  invalidDemographicRowUuids: ReadonlySet<Uuid>;
  onUpdate: (item: BuilderReportTemplateTableItem) => void;
  onUpdateRow: (rowIndex: number, updater: (row: BuilderReportTemplateTableRow) => BuilderReportTemplateTableRow) => void;
  onMoveRow: (rowIndex: number, direction: -1 | 1) => void;
  onRemoveRow: (rowIndex: number) => void;
  onToggleRowCollapsed: (rowKey: Uuid) => void;
  onToggleAddRow: (rowKey: Uuid) => void;
  onRowTypeChange: (rowKey: Uuid, type: TableRowType) => void;
  onAddRowAfter: (rowIndex: number, type: TableRowType) => void;
}>;

const TableEditor = ({
  addRowAfterKey, addRowTypeByKey, complexFormulaRowUuids, expandedRows, demographicLabelMap, demographicOptions, item, itemIndex, onAddRowAfter, onMoveRow,
  invalidDemographicRowUuids,
  onRemoveRow, onRowTypeChange, onToggleAddRow, onToggleRowCollapsed, onUpdate, onUpdateRow,
}: TableEditorProps) => (
  <section aria-label={`Template item ${itemIndex + 1} table editor`}>
    <TextField
      label="Table Title"
      ariaLabel={`Template item ${itemIndex + 1} table title`}
      value={item.title ?? ''}
      onChange={title => onUpdate({ ...item, title: title || undefined })}
    />
    <TextField
      label="Default % Metric"
      ariaLabel={`Template item ${itemIndex + 1} default percentage calculation metric`}
      value={item.default_percentage_calc_metric ?? ''}
      onChange={defaultPercentageMetric => onUpdate({
        ...item,
        default_percentage_calc_metric: defaultPercentageMetric || undefined,
      })}
    />

    {!item.content.length && (
      <InlineAddRowControl
        rowKey={item.uuid}
        itemIndex={itemIndex}
        rowIndex={-1}
        isOpen={addRowAfterKey === item.uuid}
        selectedType={addRowTypeByKey[item.uuid] ?? 'data-row'}
        onToggle={onToggleAddRow}
        onTypeChange={onRowTypeChange}
        onAdd={onAddRowAfter}
      />
    )}

    {item.content.map((row, rowIndex) => (
      <Fragment key={row.uuid}>
        <TableRowEditor
          row={row}
          itemIndex={itemIndex}
          rowIndex={rowIndex}
          rowCount={item.content.length}
          demographicOptions={demographicOptions}
          demographicLabelMap={demographicLabelMap}
          isComplexFormula={complexFormulaRowUuids.has(row.uuid)}
          isDemographicIdInvalid={invalidDemographicRowUuids.has(row.uuid)}
          isCollapsed={!expandedRows.has(row.uuid)}
          onToggleCollapsed={() => onToggleRowCollapsed(row.uuid)}
          onUpdate={updater => onUpdateRow(rowIndex, updater)}
          onMove={direction => onMoveRow(rowIndex, direction)}
          onRemove={() => onRemoveRow(rowIndex)}
        />
        <InlineAddRowControl
          rowKey={row.uuid}
          itemIndex={itemIndex}
          rowIndex={rowIndex}
          isOpen={addRowAfterKey === row.uuid}
          selectedType={addRowTypeByKey[row.uuid] ?? 'data-row'}
          onToggle={onToggleAddRow}
          onTypeChange={onRowTypeChange}
          onAdd={onAddRowAfter}
        />
      </Fragment>
    ))}
  </section>
);

type TableRowEditorProps = Readonly<{
  row: BuilderReportTemplateTableRow;
  itemIndex: number;
  rowIndex: number;
  rowCount: number;
  demographicOptions: ReadonlyArray<DemographicOption>;
  demographicLabelMap: ReadonlyMap<string, string>;
  isComplexFormula: boolean;
  isDemographicIdInvalid: boolean;
  isCollapsed: boolean;
  onToggleCollapsed: () => void;
  onUpdate: (updater: (row: BuilderReportTemplateTableRow) => BuilderReportTemplateTableRow) => void;
  onMove: (direction: -1 | 1) => void;
  onRemove: () => void;
}>;

const TableRowEditor = ({
  demographicLabelMap, demographicOptions, isCollapsed, isComplexFormula, isDemographicIdInvalid, itemIndex, onMove, onRemove, onToggleCollapsed, onUpdate, row, rowCount, rowIndex,
}: TableRowEditorProps) => (
  <article css={nestedItemStyle} aria-label={`Template item ${itemIndex + 1} row ${rowIndex + 1} editor`}>
    <div css={editorHeaderStyle}>
      <button
        css={isDemographicIdInvalid || isComplexFormula ? titleButtonErrorStyle : titleButtonStyle}
        type="button"
        aria-label={`${isCollapsed ? 'Expand' : 'Collapse'} table ${itemIndex + 1} row ${rowIndex + 1}`}
        aria-expanded={!isCollapsed}
        onClick={onToggleCollapsed}
      >{`${isCollapsed ? '+' : '-'} Row ${rowIndex + 1}: ${getRowSummary(row)}`}
      </button>
      <div css={editorActionsStyle}>
        <button
          css={buttonStyle}
          type="button"
          aria-label={`Move table ${itemIndex + 1} row ${rowIndex + 1} up`}
          disabled={rowIndex === 0}
          onClick={() => onMove(-1)}
        >Up
        </button>
        <button
          css={buttonStyle}
          type="button"
          aria-label={`Move table ${itemIndex + 1} row ${rowIndex + 1} down`}
          disabled={rowIndex === rowCount - 1}
          onClick={() => onMove(1)}
        >Down
        </button>
        <button
          css={dangerButtonStyle}
          type="button"
          aria-label={`Remove table ${itemIndex + 1} row ${rowIndex + 1}`}
          onClick={onRemove}
        >Remove
        </button>
      </div>
    </div>

    {!isCollapsed && (
      <div css={bodyStyle}>
        {row.type === 'title-row' && (
          <TextField
            label="Content"
            ariaLabel={`Template item ${itemIndex + 1} row ${rowIndex + 1} title content`}
            value={row.content}
            onChange={content => onUpdate(() => ({ ...row, content }))}
          />
        )}

        {row.type === 'data-row' && (
          <DataRowEditor
            row={row}
            itemIndex={itemIndex}
            rowIndex={rowIndex}
            demographicOptions={demographicOptions}
            demographicLabelMap={demographicLabelMap}
            isComplexFormula={isComplexFormula}
            isDemographicIdInvalid={isDemographicIdInvalid}
            onUpdate={nextRow => onUpdate(() => ({ ...nextRow, uuid: row.uuid }))}
          />
        )}
      </div>
    )}
  </article>
);

type InlineAddItemControlProps = Readonly<{
  afterIndex: number;
  isOpen: boolean;
  selectedType: TemplateItemType;
  onToggle: () => void;
  onTypeChange: (type: TemplateItemType) => void;
  onAdd: (type: TemplateItemType) => void;
}>;

const InlineAddItemControl = ({ afterIndex, isOpen, onAdd, onToggle, onTypeChange, selectedType }: InlineAddItemControlProps) => (
  <div css={inlineAddWrapperStyle}>
    {!isOpen && (
      <button
        css={linkButtonStyle}
        type="button"
        aria-label={afterIndex === -1 ? 'Add first template item' : `Add template item below item ${afterIndex + 1}`}
        onClick={onToggle}
      >{afterIndex === -1 ? '+ Add first item' : '+ Add item below'}
      </button>
    )}
    {isOpen && (
      <div css={inlineAddPanelStyle} aria-label={afterIndex === -1 ? 'Add first template item panel' : `Add template item below item ${afterIndex + 1} panel`}>
        <select
          css={inputStyle}
          aria-label={afterIndex === -1 ? 'First template item type' : `Template item type below item ${afterIndex + 1}`}
          value={selectedType}
          onChange={(event) => onTypeChange(event.target.value as TemplateItemType)}
        >
          {itemTypeOptions.map(type => <option key={type} value={type}>{type}</option>)}
        </select>
        <button
          css={buttonStyle}
          type="button"
          aria-label={afterIndex === -1 ? 'Confirm add first template item' : `Confirm add template item below item ${afterIndex + 1}`}
          onClick={() => onAdd(selectedType)}
        >Add Item
        </button>
        <button
          css={linkButtonStyle}
          type="button"
          aria-label="Cancel adding template item"
          onClick={onToggle}
        >Cancel
        </button>
      </div>
    )}
  </div>
);

type InlineAddRowControlProps = Readonly<{
  rowKey: Uuid;
  itemIndex: number;
  rowIndex: number;
  isOpen: boolean;
  selectedType: TableRowType;
  onToggle: (rowKey: Uuid) => void;
  onTypeChange: (rowKey: Uuid, type: TableRowType) => void;
  onAdd: (rowIndex: number, type: TableRowType) => void;
}>;

const InlineAddRowControl = ({
  isOpen, itemIndex, onAdd, onToggle, onTypeChange, rowIndex, rowKey, selectedType,
}: InlineAddRowControlProps) => (
  <div css={inlineAddWrapperStyle}>
    {!isOpen && (
      <button
        css={linkButtonStyle}
        type="button"
        aria-label={rowIndex === -1 ? `Add first row to table ${itemIndex + 1}` : `Add row below table ${itemIndex + 1} row ${rowIndex + 1}`}
        onClick={() => onToggle(rowKey)}
      >{rowIndex === -1 ? '+ Add first row' : '+ Add row below'}
      </button>
    )}
    {isOpen && (
      <div css={inlineAddPanelStyle} aria-label={rowIndex === -1 ? `Add first row to table ${itemIndex + 1} panel` : `Add row below table ${itemIndex + 1} row ${rowIndex + 1} panel`}>
        <select
          css={inputStyle}
          aria-label={rowIndex === -1 ? `First row type for table ${itemIndex + 1}` : `Row type below table ${itemIndex + 1} row ${rowIndex + 1}`}
          value={selectedType}
          onChange={(event) => onTypeChange(rowKey, event.target.value as TableRowType)}
        >
          {tableRowTypeOptions.map(type => <option key={type} value={type}>{type}</option>)}
        </select>
        <button
          css={buttonStyle}
          type="button"
          aria-label={rowIndex === -1 ? `Confirm add first row to table ${itemIndex + 1}` : `Confirm add row below table ${itemIndex + 1} row ${rowIndex + 1}`}
          onClick={() => onAdd(rowIndex, selectedType)}
        >Add Row
        </button>
        <button
          css={linkButtonStyle}
          type="button"
          aria-label="Cancel adding table row"
          onClick={() => onToggle(rowKey)}
        >Cancel
        </button>
      </div>
    )}
  </div>
);

const getItemSummary = (item: ReportTemplateItem): string => {
  switch (item.type) {
    case 'title':
      return `title - ${item.content}`;
    case 'table':
      return item.title ? `table - ${item.title}` : `table - ${item.content.length} rows`;
    case 'map':
      return 'map';
    case 'line-break':
      return 'line break';
    case 'page-break':
      return 'page break';
    default:
      throw new Error(`Unsupported template item type: ${item satisfies never}`);
  }
};

const getRowSummary = (row: ReportTemplateTableRow): string => {
  switch (row.type) {
    case 'data-row':
      return row.custom_label ? `data row - ${row.custom_label}` : `data row - ${row.id}`;
    case 'title-row':
      return `title row - ${row.content}`;
    case 'empty-row':
      return 'empty row';
    default:
      throw new Error(`Unsupported table row type: ${row satisfies never}`);
  }
};

type DataRowEditorProps = Readonly<{
  row: ReportTemplateDataRow;
  itemIndex: number;
  rowIndex: number;
  demographicOptions: ReadonlyArray<DemographicOption>;
  demographicLabelMap: ReadonlyMap<string, string>;
  isComplexFormula: boolean;
  isDemographicIdInvalid: boolean;
  onUpdate: (row: ReportTemplateDataRow) => void;
}>;

const DataRowEditor = ({ demographicLabelMap, demographicOptions, isComplexFormula, isDemographicIdInvalid, itemIndex, onUpdate, row, rowIndex }: DataRowEditorProps) => {
  const label = row.custom_label ?? demographicLabelMap.get(row.id) ?? 'Custom or formula row';
  const ariaPrefix = `Template item ${itemIndex + 1} row ${rowIndex + 1}`;

  return (
    <>
      <DemographicIdField
        ariaPrefix={ariaPrefix}
        demographicOptions={demographicOptions}
        value={row.id}
        onChange={id => onUpdate({ ...row, id })}
      />
      <div css={isDemographicIdInvalid ? errorTextStyle : mutedTextStyle} aria-label={`${ariaPrefix} demographic label`}>{label}</div>
      <TextField
        label="Custom Label"
        ariaLabel={`${ariaPrefix} custom label`}
        value={row.custom_label ?? ''}
        onChange={custom_label => onUpdate({ ...row, custom_label: custom_label || undefined })}
      />
      <TextField
        label="Formula"
        ariaLabel={`${ariaPrefix} formula`}
        value={row.formula ?? ''}
        onChange={formula => onUpdate({ ...row, formula: formula || undefined })}
      />
      {isComplexFormula && <div css={errorTextStyle} aria-label={`${ariaPrefix} formula error`}>Formula is too complex for report system.</div>}
      <TextField
        label="% Metric"
        ariaLabel={`${ariaPrefix} percentage calculation metric`}
        value={row.percentage_calc_metric ?? ''}
        onChange={percentage_calc_metric => onUpdate({ ...row, percentage_calc_metric: percentage_calc_metric || undefined })}
      />
      <TextField
        label="Unit"
        ariaLabel={`${ariaPrefix} unit`}
        value={row.unit ?? ''}
        onChange={unit => onUpdate({ ...row, unit: unit || undefined })}
      />
      <Field label="Format Type">
        <select
          css={inputStyle}
          aria-label={`${ariaPrefix} format type`}
          value={row.format_type ?? ''}
          onChange={(event) => onUpdate({
            ...row,
            format_type: event.target.value ? event.target.value as ReportTemplateDataRow['format_type'] : undefined,
          })}
        >
          <option value="">Auto</option>
          {formatTypeOptions.map(type => <option key={type} value={type}>{type}</option>)}
        </select>
      </Field>
      <Field label="Style">
        <select
          css={inputStyle}
          aria-label={`${ariaPrefix} style`}
          value={row.style ?? ''}
          onChange={(event) => onUpdate({
            ...row,
            style: event.target.value ? event.target.value as ReportTemplateDataRow['style'] : undefined,
          })}
        >
          <option value="">Regular</option>
          <option value="bold">Bold</option>
        </select>
      </Field>
      <label css={checkboxRowStyle}>
        <input
          css={checkboxStyle}
          type="checkbox"
          aria-label={`${ariaPrefix} include percentage value`}
          checked={!!row.include_percentage_value}
          onChange={() => onUpdate({ ...row, include_percentage_value: row.include_percentage_value ? undefined : true })}
        />
        Include percentage value
      </label>
    </>
  );
};

type DemographicIdFieldProps = Readonly<{
  ariaPrefix: string;
  demographicOptions: ReadonlyArray<DemographicOption>;
  value: string;
  onChange: (value: string) => void;
}>;

const DemographicIdField = ({ ariaPrefix, demographicOptions, onChange, value }: DemographicIdFieldProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const suggestions = useMemo(() => filterDemographicOptions(demographicOptions, value).slice(0, 25), [demographicOptions, value]);
  const listId = `${ariaPrefix.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-demographic-options`;

  return (
    <Field label="Demographic ID">
      <div css={inputDropdownWrapperStyle}>
        <input
          css={inputStyle}
          role="combobox"
          aria-label={`${ariaPrefix} demographic ID`}
          aria-autocomplete="list"
          aria-controls={listId}
          aria-expanded={isOpen}
          value={value}
          onBlur={() => setIsOpen(false)}
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            onChange(event.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
        />
        {isOpen && !!suggestions.length && (
          <div
            css={inputDropdownStyle}
            id={listId}
            role="listbox"
            aria-label={`${ariaPrefix} demographic ID suggestions`}
          >
            {suggestions.map(option => (
              <button
                css={inputDropdownOptionStyle}
                key={`${option.region}-${option.id}`}
                type="button"
                role="option"
                aria-selected={option.id === value}
                aria-label={`Use ${option.id} ${option.label}`}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => {
                  onChange(value.startsWith('%') ? `%${option.id}` : option.id);
                  setIsOpen(false);
                }}
              >
                {`${option.id} - ${option.label}`}
                <span css={inputDropdownMetaStyle}>{`${option.category} / ${option.region}`}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </Field>
  );
};
